import type { PortablePlacement } from "@/api/portableApplicationClient";
import { markBootedSignedOut } from "./discovery/signedOutBoot";
import { publicTrialSeconds } from "@/app/publicTrialPolicy";
import { accountApiBase, fetchWithConnectionAuth } from "@/app/connection";
import {
  fetchWithCookieAuth,
  withCookieAuth,
  withoutCredentials,
} from "@/app/connection/cookieFetch";
import { traceStartupUrl } from "@/performance/httpTiming";
import { appStartup } from "@/performance/startupTiming";
import type {
  AppSourcePresentation,
  DiscoverPublication,
  PersonalApp,
} from "@/components/personal-apps/types";
import {
  importDiscoverPortableApplication,
  type PortableImportRecovery,
} from "@/api/portableApplicationClient";

/** Proposed additive contract; absent fields remain unavailable in the UI. */
export interface DiscoverStats {
  discover_run_count?: number | null;
  install_count?: number | null;
  run_count: number | null;
  run_count_status?: "recorded" | "not_tracked";
  comment_count: number | null;
  like_count: number | null;
  as_of: string;
}

export interface DiscoverApp {
  public_trial?: import("@/app/publicTrialPolicy").PublicTrialPolicy;
  ai_rating?: {
    version_ref: string;
    average: number | null;
    rated_count: number;
  };
  source?: AppSourcePresentation;
  published_version?: string;
  preview_video?: {
    url: string;
    poster_url: string;
    content_type: "video/mp4" | "video/webm";
    published_version: string;
    width: number;
    height: number;
    duration_ms: number;
    byte_size: number;
  } | null;
  developers?: DiscoverDeveloper[];
  license?: string | null;
  getting_started?: string | null;
  stats?: DiscoverStats;
  viewer?: { liked: boolean; can_like: boolean };
  execution_class?:
    import("@/components/compute/appAccess").AppExecutionClass | null;
  runtime_state?: "running" | "ready" | "sleeping" | "starting" | "failed";
  id: string;
  /** Optional capsule slug and public detail link. Older APIs may return a legacy link. */
  app_slug: string | null;
  app_path: string | null;
  publisher_handle: string | null;
  title: string;
  description: string;
  /** Backend-owned catalogue source classification. Treat unknown/new kinds
   *  as open catalogue entries unless a surface has an explicit behavior for
   *  them; staging also serves portable catalogue variants. */
  source_kind: string;
  /** Backend guarantee that Try creates a bounded, non-durable session. */
  public_try_available?: boolean;
  try_first_action?: string | null;
  /** Server-resolved public static delivery, independent of owned app_url. */
  public_preview_url?: string | null;
  public_access_available?: boolean;
  source_url: string | null;
  thumbnail_url: string | null;
  icon: string | null;
  /** Who published a community listing. Null for the curated catalogue. */
  creator_name: string | null;
  published_at: string | null;
  added: boolean;
  instance_id: string | null;
  app_url: string | null;
  /** Server-assigned genre (editorial or inferred); null when unclassified. */
  category?: string | null;
  /** Executable content kind, independent of genre; null is an ordinary app. */
  content_type?:
    import("./discovery/discoverContent").DiscoverContentType | null;
  /** Publisher-declared detail facts; only what was declared. */
  content_details?:
    import("./discovery/discoverContent").DiscoverContentDetails | null;
  /** Detail reads only: a Continue state and the listing it was saved from. */
  related?: import("./discovery/discoverContent").DiscoverRelatedListing[];
}

export interface PublicAppAdmission {
  trialLimitSeconds?: number | null;
  appUrl: string | null;
  instanceId: string | null;
  error: string | null;
  expiresAt?: string;
}

export interface DiscoverStartup {
  app: DiscoverApp | null;
  public_app?: PublicAppAdmission | null;
}

export interface DiscoverDeveloper {
  role?: "publisher" | "repository_owner" | "author" | "modifier";
  repository_url?: string;
  id: string;
  name: string;
  avatar_url: string | null;
  profile_url: string;
}

export interface DiscoverPage {
  apps: DiscoverApp[];
  next_cursor: string | null;
  categories: { id: string; label: string }[];
  developer?: DiscoverDeveloper | null;
}

export interface DiscoverPageOptions {
  limit?: number;
  cursor?: string;
  exclude?: string;
  developer?: string;
  q?: string;
  category?: string;
  /** `popular` ranks by distinct recent visitors; it backs the visible
   *  Trending tab. Any other value falls back to the editorial order. */
  sort?: "recommended" | "new" | "rating" | "popular";
}

/**
 * A failed Discover call, with the server's own code kept.
 *
 * The code is what the screen maps to a sentence somebody can act on —
 * "somebody already published this App" is a different situation from "this
 * app has no finished build", and one message for both would tell a person
 * nothing about which one they are in.
 */
export class DiscoverApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message?: string,
  ) {
    super(message || code);
  }
}

export type AddDiscoverAppResponse =
  | {
      result: "added" | "already_added";
      instance: PersonalApp;
    }
  | {
      result: "portable_import_required";
      portable: {
        bundle_id: string;
        bundle_sha256: string;
        contract_ref: string;
        derivation_ref: string;
        file_name: string;
        requires_gpu?: boolean;
      };
    };

async function jsonRequest<T>(
  path: string,
  method: "GET" | "POST" | "PUT" | "DELETE",
  body?: unknown,
  options: {
    signal?: AbortSignal;
    publicMetadata?: boolean;
    guestAdmission?: boolean;
    headers?: Record<string, string>;
  } = {},
): Promise<T> {
  const request =
    options.publicMetadata || options.guestAdmission
      ? fetch
      : fetchWithConnectionAuth;
  const startupAttempt = appStartup().id;
  const requestUrl = /\/start$/.test(path)
    ? traceStartupUrl(`${accountApiBase()}${path}`, startupAttempt)
    : `${accountApiBase()}${path}`;
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (options.signal?.aborted) abort();
  options.signal?.addEventListener("abort", abort, { once: true });
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, 30_000);
  try {
    const init: RequestInit = {
      method,
      headers: {
        Accept: "application/json",
        ...options.headers,
        ...(body === undefined ? {} : { "Content-Type": "application/json" }),
      },
      cache: "no-store",
      signal: controller.signal,
      body: body === undefined ? undefined : JSON.stringify(body),
    };
    const response = await request(
      requestUrl,
      options.guestAdmission
        ? withCookieAuth(init)
        : options.publicMetadata
          ? withoutCredentials(init)
          : init,
    );
    const payload = (await response.json().catch(() => ({}))) as {
      error?: string;
      message?: string;
    };
    if (!response.ok) {
      throw new DiscoverApiError(
        response.status,
        payload.error ?? `http_${response.status}`,
        payload.message,
      );
    }
    return payload as T;
  } catch (error) {
    if (timedOut) throw new DiscoverApiError(0, "request_timeout");
    throw error;
  } finally {
    clearTimeout(timer);
    options.signal?.removeEventListener("abort", abort);
  }
}

function discoverPagePath(options: DiscoverPageOptions): string {
  const query = new URLSearchParams();
  query.set("limit", String(options.limit ?? 12));
  if (options.cursor) query.set("cursor", options.cursor);
  if (options.exclude) query.set("exclude", options.exclude);
  if (options.developer) query.set("developer", options.developer);
  if (options.q) query.set("q", options.q);
  if (options.category) query.set("category", options.category);
  if (options.sort) query.set("sort", options.sort);
  return `/v1/discover/apps/page?${query}`;
}

/** A boot-time read is only reused while it is this fresh. */
const PREFETCH_TTL_MS = 30_000;
const prefetchedPages = new Map<
  string,
  { at: number; take: () => Promise<DiscoverPage> }
>();

/**
 * Start a Discover page read at boot, alongside the session read, so the first
 * screen does not wait for one round trip before starting the other. The first
 * matching `discoverClient.page()` call takes it; the cookie it was sent with
 * is the one the session read sees, so viewer fields agree.
 */
export function prefetchDiscoverPage(options: DiscoverPageOptions): void {
  const path = discoverPagePath(options);
  if (prefetchedPages.has(path)) return;
  const page = jsonRequest<DiscoverPage>(path, "GET");
  page.catch(() => {});
  prefetchedPages.set(path, { at: Date.now(), take: () => page });
}

/**
 * Take the page the server put in the HTML. It is only there for a visitor
 * with no session cookie (worker/index.ts), so it is the signed-out page, and
 * that visitor's catalogue need not wait for the session read to say so.
 */
export function adoptEmbeddedDiscoverPage(
  options: DiscoverPageOptions,
  page: DiscoverPage,
): void {
  // Handed over only after the screen around it has painted: drawing every
  // card inside the app's first commit holds back that first paint on a slow
  // phone, while one frame later costs the cards almost nothing.
  prefetchedPages.set(discoverPagePath(options), {
    at: Date.now(),
    take: () => afterNextPaint().then(() => page),
  });
  markBootedSignedOut();
}

function afterNextPaint(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame !== "function") return resolve();
    requestAnimationFrame(() => setTimeout(resolve, 0));
  });
}

function takePrefetchedPage(path: string): Promise<DiscoverPage> | null {
  const entry = prefetchedPages.get(path);
  prefetchedPages.delete(path);
  return entry && Date.now() - entry.at < PREFETCH_TTL_MS ? entry.take() : null;
}

function withAbort<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return promise;
  return new Promise<T>((resolve, reject) => {
    const abort = () => reject(new DOMException("Aborted", "AbortError"));
    if (signal.aborted) return abort();
    signal.addEventListener("abort", abort, { once: true });
    promise
      .then(resolve, reject)
      .finally(() => signal.removeEventListener("abort", abort));
  });
}

export interface DiscoverAnalyticsEventInput {
  event_id: string;
  discover_app_id: string;
  kind: "impression" | "detail";
  published_version: string;
  attribution: DiscoveryAttribution;
  /** 1-based position in the rendered feed; impressions only. */
  position?: number;
  /** Which list produced the impression: all|popular|new|for_you|category|hero. */
  view?: string;
  /** The impression this detail event belongs to. */
  exposure_id?: string;
}

export interface DiscoverAnalyticsEventsResult {
  recorded: number;
  duplicates: number;
  skipped: number;
}

/** A hidden page cannot recover a result, so missing counts read as zero. */
const emptyAnalyticsEventsResult: DiscoverAnalyticsEventsResult = {
  recorded: 0,
  duplicates: 0,
  skipped: 0,
};

function isAnalyticsEventsResult(
  value: unknown,
): value is DiscoverAnalyticsEventsResult {
  if (value === null || typeof value !== "object" || Array.isArray(value))
    return false;
  return ["recorded", "duplicates", "skipped"].every((key) => {
    const count = (value as Record<string, unknown>)[key];
    return (
      typeof count === "number" && Number.isSafeInteger(count) && count >= 0
    );
  });
}

function normalizePublication(
  publication: DiscoverPublication,
): DiscoverPublication {
  // An API older than source visibility omits the field; `open` is the
  // correct pre-feature default and keeps the Publish dialog honest.
  return {
    ...publication,
    source_visibility: publication.source_visibility ?? "open",
  };
}

function validatePublicAdmission(
  result: PublicAppAdmission,
): PublicAppAdmission {
  if (
    result.trialLimitSeconds !== undefined &&
    result.trialLimitSeconds !== null &&
    (!Number.isInteger(result.trialLimitSeconds) ||
      result.trialLimitSeconds < 1 ||
      result.trialLimitSeconds > 86_400)
  )
    throw new DiscoverApiError(502, "invalid_public_app_response");
  if (result.error != null) {
    if (
      typeof result.error !== "string" ||
      result.appUrl !== null ||
      result.instanceId !== null
    )
      throw new DiscoverApiError(502, "invalid_public_app_response");
    return result;
  }
  try {
    const url = new URL(result.appUrl ?? "");
    if (
      url.protocol !== "https:" ||
      url.username ||
      url.password ||
      typeof result.instanceId !== "string" ||
      !result.instanceId ||
      !Number.isFinite(Date.parse(result.expiresAt ?? ""))
    )
      throw new Error();
  } catch {
    throw new DiscoverApiError(502, "invalid_public_app_response");
  }
  return result;
}

export const discoverClient = {
  creatorPublications(cursor?: string, signal?: AbortSignal) {
    return jsonRequest<{
      publications: CreatorPublication[];
      next_cursor: string | null;
    }>(
      `/v1/discover/creator/publications${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
      "GET",
      undefined,
      { signal },
    );
  },
  creatorAnalytics(id: string, days: 7 | 28 | 90, signal?: AbortSignal) {
    return jsonRequest<CreatorAnalytics>(
      `/v1/discover/creator/publications/${encodeURIComponent(id)}/analytics?days=${days}`,
      "GET",
      undefined,
      { signal },
    ).then((data) => {
      if (!validCreatorAnalytics(data, id, days))
        throw new DiscoverApiError(
          502,
          "invalid_creator_analytics_response",
          "Analytics could not be loaded.",
        );
      return data;
    });
  },
  analyticsEvent(
    id: string,
    input: {
      event_id: string;
      kind: "impression" | "detail";
      published_version: string;
      attribution: DiscoveryAttribution;
      /** The impression this detail event belongs to. */
      exposure_id?: string;
    },
  ) {
    return jsonRequest<{ recorded: boolean }>(
      `/v1/discover/apps/${encodeURIComponent(id)}/analytics-events`,
      "POST",
      input,
    );
  },
  /**
   * Record several measurements in one request.
   *
   * Card-display impressions are batched so a screenful of cards costs one
   * write instead of one per card. `keepalive` is for the `pagehide` flush: the
   * document is going away, so the request is best-effort and transport
   * failures are swallowed rather than becoming an unhandled rejection.
   */
  analyticsEvents(
    events: readonly DiscoverAnalyticsEventInput[],
    options: { keepalive?: boolean } = {},
  ): Promise<DiscoverAnalyticsEventsResult> {
    if (options.keepalive) {
      return fetchWithCookieAuth(
        `${accountApiBase()}/v1/discover/analytics-events`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ events }),
          keepalive: true,
        },
      )
        .then(async (response) => {
          if (!response.ok) return emptyAnalyticsEventsResult;
          const payload = (await response.json().catch(() => null)) as unknown;
          return isAnalyticsEventsResult(payload)
            ? payload
            : emptyAnalyticsEventsResult;
        })
        .catch(() => emptyAnalyticsEventsResult);
    }
    return jsonRequest<DiscoverAnalyticsEventsResult>(
      "/v1/discover/analytics-events",
      "POST",
      { events },
    );
  },
  /**
   * Signed-in feedback. Anonymous visitors never call these: they keep the
   * same choices in localStorage (see discovery/discoverFeedback.ts).
   */
  hideApp(id: string) {
    return jsonRequest<{ hidden?: boolean }>(
      `/v1/discover/apps/${encodeURIComponent(id)}/hidden`,
      "PUT",
    );
  },
  unhideApp(id: string) {
    return jsonRequest<{ hidden?: boolean }>(
      `/v1/discover/apps/${encodeURIComponent(id)}/hidden`,
      "DELETE",
    );
  },
  rateApp(id: string, rating: "helpful" | "not_interested") {
    return jsonRequest<{ rating?: string | null }>(
      `/v1/discover/apps/${encodeURIComponent(id)}/rating`,
      "PUT",
      { rating },
    );
  },
  clearRating(id: string) {
    return jsonRequest<{ rating?: string | null }>(
      `/v1/discover/apps/${encodeURIComponent(id)}/rating`,
      "DELETE",
    );
  },
  hiddenApps() {
    return jsonRequest<{ apps?: { id: string; title: string }[] }>(
      "/v1/discover/me/hidden",
      "GET",
    ).then((result) => ({
      apps: Array.isArray(result?.apps) ? result.apps : [],
    }));
  },
  launch(id: string | null, signal?: AbortSignal) {
    const attempt = appStartup().id;
    return jsonRequest<DiscoverStartup>(
      id
        ? `/v1/discover/apps/${encodeURIComponent(id)}/start`
        : "/v1/discover/entry/start",
      "POST",
      undefined,
      { signal, guestAdmission: true },
    ).then((result) => {
      if (result.public_app != null) {
        validatePublicAdmission(result.public_app);
        if (result.public_app.appUrl)
          result.public_app.appUrl = traceStartupUrl(
            result.public_app.appUrl,
            attempt,
          );
      }
      return result;
    });
  },
  detail(id: string, signal?: AbortSignal) {
    return jsonRequest<{ app: DiscoverApp }>(
      `/v1/discover/apps/${encodeURIComponent(id)}`,
      "GET",
      undefined,
      { signal },
    );
  },
  byCapsule(id: string, signal?: AbortSignal) {
    return jsonRequest<{ app: DiscoverApp | null }>(
      `/v1/discover/capsules/${encodeURIComponent(id)}/app`,
      "GET",
      undefined,
      { signal },
    );
  },
  page(options: DiscoverPageOptions = {}, signal?: AbortSignal) {
    const path = discoverPagePath(options);
    const early = takePrefetchedPage(path);
    if (early) return withAbort(early, signal);
    return jsonRequest<DiscoverPage>(path, "GET", undefined, { signal });
  },
  beginUsage(
    id: string,
    input: {
      request_id: string;
      confirmation_key: string;
      published_version: string;
      attribution?: DiscoveryAttribution;
      /** The impression whose card led to this Open. */
      exposure_id?: string;
    },
  ) {
    return jsonRequest<{
      discovery_session_id: string;
      published_version: string;
      counted: boolean;
    }>(
      `/v1/discover/apps/${encodeURIComponent(id)}/discovery-sessions`,
      "POST",
      input,
    ).then((result) => {
      if (
        !/^ds_[A-Za-z0-9]+$/.test(result.discovery_session_id) ||
        result.published_version !== input.published_version ||
        typeof result.counted !== "boolean"
      )
        throw new DiscoverApiError(502, "invalid_discovery_session_response");
      return result;
    });
  },
  confirmUsage(
    id: string,
    sessionId: string,
    key: string,
    surface: {
      kind: "browser" | "static" | "hosted" | "shared";
      instance_id?: string;
    },
  ) {
    return jsonRequest<{
      counted: boolean;
      newly_counted: boolean;
      discover_run_count: number;
    }>(
      `/v1/discover/apps/${encodeURIComponent(id)}/discovery-sessions/${encodeURIComponent(sessionId)}/ready`,
      "POST",
      { confirmation_key: key, surface },
    ).then((result) => {
      if (
        result.counted !== true ||
        !Number.isSafeInteger(result.discover_run_count) ||
        result.discover_run_count < 0
      )
        throw new DiscoverApiError(502, "invalid_discover_usage_response");
      return result;
    });
  },
  confirmInstall(id: string, instanceId: string) {
    return jsonRequest<{ recorded: boolean }>(
      `/v1/discover/apps/${encodeURIComponent(id)}/install-confirmation`,
      "POST",
      { instance_id: instanceId },
    ).then((result) => {
      if (result.recorded !== true)
        throw new DiscoverApiError(502, "invalid_discover_install_response");
      return result;
    });
  },
  stopPublicGuestTry(app: PersonalApp) {
    return jsonRequest<{ public_try_cleanup: { resources_released: boolean } }>(
      `/v1/capsule-bundles/${encodeURIComponent(app.public_guest_try!.bundle_id)}/public-try/stop`,
      "POST",
      { instance_id: app.instance_id },
    );
  },
  openPublic(id: string, signal?: AbortSignal) {
    return jsonRequest<{
      appUrl: string;
      instanceId: string;
      expiresAt: string;
      trialLimitSeconds?: number | null;
    }>(`/v1/discover/apps/${encodeURIComponent(id)}/open`, "POST", undefined, {
      signal,
    }).then((result) => {
      validatePublicAdmission({ ...result, error: null });
      return result;
    });
  },
  /** Report a listing; signed-in only, one report per person and version. */
  report(id: string, body: { reason: string; details?: string }) {
    return jsonRequest<{ report_id: string; applied: boolean; status: string }>(
      `/v1/discover/apps/${encodeURIComponent(id)}/reports`,
      "POST",
      {
        reason: body.reason,
        ...(body.details?.trim() ? { details: body.details.trim() } : {}),
      },
    );
  },
  setLike(id: string, liked: boolean) {
    return jsonRequest<{ liked: boolean; like_count: number }>(
      `/v1/discover/apps/${encodeURIComponent(id)}/like`,
      liked ? "PUT" : "DELETE",
    ).then((result) => {
      if (
        typeof result.liked !== "boolean" ||
        !Number.isSafeInteger(result.like_count) ||
        result.like_count < 0
      )
        throw new DiscoverApiError(502, "invalid_like_response");
      return result;
    });
  },
  add(id: string) {
    return jsonRequest<AddDiscoverAppResponse>(
      `/v1/discover/apps/${encodeURIComponent(id)}/add`,
      "POST",
    );
  },

  startTry(id: string) {
    return jsonRequest<
      Extract<
        AddDiscoverAppResponse,
        { result: "portable_import_required" }
      > & { public_trial?: import("@/app/publicTrialPolicy").PublicTrialPolicy }
    >(`/v1/discover/apps/${encodeURIComponent(id)}/try`, "POST");
  },

  /**
   * Publish one of my Apps, or re-publish it with different words.
   *
   * The instance id is what this screen has; what gets published is the App
   * behind it, resolved server-side. Sending the same App twice updates one
   * listing rather than making a second — the catalogue, not the client, is
   * where that is enforced.
   *
   * `source_visibility` omitted means server-side "open on create, keep the
   * current policy on update"; it is always sent explicitly from the dialog so
   * a private publication is never silently reopened by an unrelated edit.
   */
  publish(
    instanceId: string,
    listing: {
      title: string;
      description: string;
      sourceVisibility: "open" | "private";
      action?: "publish" | "metadata";
      expectedVersion?: number | null;
      schemaId?: string;
    },
    requestKey?: string,
  ) {
    return jsonRequest<{ publication: DiscoverPublication }>(
      "/v1/discover/apps",
      "POST",
      {
        instance_id: instanceId,
        title: listing.title,
        description: listing.description,
        source_visibility: listing.sourceVisibility,
        action: listing.action,
        expected_version: listing.expectedVersion,
        schema_id: listing.schemaId,
      },
      { headers: requestKey ? { "Idempotency-Key": requestKey } : {} },
    ).then((result) => ({
      publication: normalizePublication(result.publication),
    }));
  },

  unpublish(id: string, expectedVersion?: number) {
    return jsonRequest<{ publication: DiscoverPublication }>(
      `/v1/discover/apps/${encodeURIComponent(id)}/unpublish`,
      "POST",
      expectedVersion === undefined
        ? undefined
        : { expected_version: expectedVersion },
    ).then((result) => ({
      publication: normalizePublication(result.publication),
    }));
  },

  /**
   * How a PUBLIC listing's source is shown or handed out.
   *
   * Public and unauthenticated: a private source answers with only
   * `{ visibility: "private" }`, so nothing about it is revealed here.
   */
  getSource(id: string) {
    return jsonRequest<AppSourcePresentation>(
      `/v1/discover/apps/${encodeURIComponent(id)}/source`,
      "GET",
    );
  },
};

/** Install either catalogue representation as one durable Personal App. */
export async function addDiscoverReadyApp(
  id: string,
  signal: AbortSignal = new AbortController().signal,
  resolveGpuPlacement?: (signal: AbortSignal) => Promise<PortablePlacement>,
): Promise<PersonalApp> {
  const response = await discoverClient.add(id);
  if (response.result !== "portable_import_required") {
    return response.instance;
  }
  return (
    await importDiscoverPortableApplication(
      response.portable,
      () => {},
      signal,
      { publicTry: false, resolveGpuPlacement },
    )
  ).app;
}

/** Start a bounded Try without ever entering the durable classic Add path. */
export async function startDiscoverPublicTry(
  id: string,
  signal: AbortSignal = new AbortController().signal,
  options: {
    publicGuest?: boolean;
    resolveGpuPlacement?: (signal: AbortSignal) => Promise<PortablePlacement>;
    onAllocated?: (app: PersonalApp) => void;
    onPending?: (recover: () => Promise<PortableImportRecovery>) => void;
    onProgress?: (message: string) => void;
  } = {},
): Promise<PersonalApp> {
  const response = await discoverClient.startTry(id);
  return (
    await importDiscoverPortableApplication(
      response.portable,
      () => {},
      signal,
      {
        publicTry: true,
        trialLimitSeconds: publicTrialSeconds(response.public_trial),
        publicGuest: options.publicGuest,
        ...(options.resolveGpuPlacement ? { resolveGpuPlacement: options.resolveGpuPlacement } : {}),
        ...(options.onAllocated ? { onAllocated: options.onAllocated } : {}),
        ...(options.onPending ? { onPending: options.onPending } : {}),
        ...(options.onProgress ? { onProgress: options.onProgress } : {}),
      },
    )
  ).app;
}

export type DiscoveryAttribution = {
  anonymous_id: string;
  visit_id: string;
  source: "discover" | "search" | "direct" | "library";
};
export type CreatorPublication = DiscoverPublication & {
  icon_url: string | null;
  instance_id: string | null;
};
export type CreatorMetrics = {
  impressions: number;
  detail_views: number;
  started_sessions: number;
  usable_sessions: number;
  unique_users: number;
  installs: number;
  unattributed_sessions: number;
  ready_rate: number | null;
  conversion: number | null;
  detail_visits: number;
  converted_visits: number;
};
export type CreatorAnalytics = {
  schema_version: 1;
  publication_id: string;
  days: 7 | 28 | 90;
  timezone: "UTC";
  from_day: string;
  to_day: string;
  previous_from_day: string;
  previous_to_day: string;
  generated_at: string;
  tracking_since: string | null;
  summary: CreatorMetrics;
  previous: CreatorMetrics;
  timeseries: Array<{
    day: string;
    impressions: number | null;
    detail_views: number | null;
    started_sessions: number | null;
    usable_sessions: number | null;
    installs: number | null;
  }>;
  traffic_sources: Array<{
    source: string;
    started_sessions: number;
    usable_sessions: number;
  }>;
  engagement: { current_installs: number; likes: number; comments: number };
  audience_scope: string;
};

function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}
const countValue = (value: unknown) =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
function validCreatorMetrics(value: unknown) {
  if (!record(value)) return false;
  return (
    [
      "impressions",
      "detail_views",
      "started_sessions",
      "usable_sessions",
      "unique_users",
      "installs",
      "unattributed_sessions",
      "detail_visits",
      "converted_visits",
    ].every((key) => countValue(value[key])) &&
    ["ready_rate", "conversion"].every(
      (key) =>
        value[key] === null ||
        (typeof value[key] === "number" &&
          Number.isFinite(value[key]) &&
          Number(value[key]) >= 0 &&
          Number(value[key]) <= 1),
    )
  );
}
function validCreatorAnalytics(
  value: unknown,
  id: string,
  days: number,
): value is CreatorAnalytics {
  if (
    !record(value) ||
    value.schema_version !== 1 ||
    value.publication_id !== id ||
    value.days !== days ||
    value.timezone !== "UTC" ||
    !validCreatorMetrics(value.summary) ||
    !validCreatorMetrics(value.previous)
  )
    return false;
  if (
    !["from_day", "to_day", "previous_from_day", "previous_to_day"].every(
      (key) =>
        typeof value[key] === "string" &&
        /^\d{4}-\d{2}-\d{2}$/.test(String(value[key])) &&
        Number.isFinite(Date.parse(String(value[key]))),
    )
  )
    return false;
  if (
    typeof value.generated_at !== "string" ||
    !Number.isFinite(Date.parse(value.generated_at))
  )
    return false;
  if (
    value.tracking_since !== null &&
    (typeof value.tracking_since !== "string" ||
      !Number.isFinite(
        Date.parse(
          value.tracking_since.replace(" ", "T") +
            (value.tracking_since.endsWith("Z") ? "" : "Z"),
        ),
      ))
  )
    return false;
  if (
    !Array.isArray(value.timeseries) ||
    value.timeseries.length !== days ||
    !value.timeseries.every(
      (point) =>
        record(point) &&
        typeof point.day === "string" &&
        [
          "impressions",
          "detail_views",
          "started_sessions",
          "usable_sessions",
          "installs",
        ].every((key) => point[key] === null || countValue(point[key])),
    )
  )
    return false;
  if (
    !Array.isArray(value.traffic_sources) ||
    !value.traffic_sources.every(
      (source) =>
        record(source) &&
        ["discover", "search", "direct", "library", "unknown"].includes(
          String(source.source),
        ) &&
        countValue(source.started_sessions) &&
        countValue(source.usable_sessions),
    )
  )
    return false;
  return (
    record(value.engagement) &&
    ["current_installs", "likes", "comments"].every((key) =>
      countValue((value.engagement as Record<string, unknown>)[key]),
    )
  );
}
